import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import { Seo } from "../components/Seo";
import { MaskedLines, FadeUp, Eyebrow } from "../components/Reveal";
import { SERVICES } from "./HomePage";

const PRICE_BLOCKS = [
  {
    id: "boiler",
    rows: [
      { label: "Fino a 300 litri", price: "CHF 390" },
      { label: "Da 301 a 500 litri", price: "CHF 590" },
      { label: "Da 501 a 800 litri", price: "CHF 790" },
      { label: "Oltre 800 litri", price: "CHF 1.10 / litro · min. CHF 890" },
    ],
    note: "Manutenzione ordinaria di un impianto accessibile e in condizioni standard. Ricambi straordinari e riparazioni esclusi.",
  },
  {
    id: "serpentine",
    rows: [
      { label: "Per circuito", price: "CHF 80" },
      { label: "Minimo d'intervento", price: "CHF 390" },
      { label: "Esempio: 10 circuiti", price: "CHF 800" },
    ],
    note: "Impianti accessibili e tecnicamente idonei al lavaggio previsto. Riparazioni, ricambi e trattamenti straordinari esclusi.",
  },
  {
    id: "filtri",
    rows: [{ label: "Filtro principale standard, cartuccia compresa", price: "CHF 150" }],
    note: "Sistemi autopulenti, filtri particolari e impianti condominiali complessi: preventivo in base al modello.",
  },
];

export default function PrezziPage() {
  return (
    <div data-testid="prezzi-page">
      <Seo
        title="Prezzi manutenzione impianti | V32 Group — Ticino"
        description="Prezzi finali in CHF per pulizia boiler, lavaggio circuiti di riscaldamento a pavimento e manutenzione filtri acqua sanitaria in Ticino. Nessun costo nascosto, prezzo confermato prima dell'intervento."
      />

      <section className="pt-32 md:pt-44 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Eyebrow>Trasparenza</Eyebrow>
          <MaskedLines
            lines={["Prezzi chiari,", "confermati in anticipo."]}
            delay={0.15}
            className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-ink max-w-3xl"
          />
          <FadeUp delay={0.5}>
            <p className="mt-8 text-ink/70 text-base md:text-lg leading-relaxed max-w-2xl">
              Prezzi finali in CHF. V32 Group non è attualmente assoggettata all'IVA
              e non addebita IVA. I prezzi si riferiscono agli interventi standard
              descritti.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {PRICE_BLOCKS.map((block, i) => {
              const service = SERVICES.find((s) => s.id === block.id);
              return (
                <FadeUp key={block.id} delay={0.08 * i}>
                  <article className="border border-ink/10 bg-white h-full flex flex-col" data-testid={`price-card-${block.id}`}>
                    <div className="p-8 pb-6 border-b border-ink/10">
                      <p className="font-mono text-xs text-bronzo tracking-[0.25em]">0{i + 1}</p>
                      <h2 className="mt-3 font-display font-medium text-xl text-ink leading-snug">
                        {service.name}
                      </h2>
                    </div>
                    <div className="p-8 pt-6 flex-1">
                      <ul className="space-y-4">
                        {block.rows.map((r) => (
                          <li key={r.label} className="flex items-baseline justify-between gap-4">
                            <span className="text-sm text-ink/60">{r.label}</span>
                            <span className="font-display font-medium text-lg text-antracite text-right">
                              {r.price}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-6 text-xs text-ink/50 leading-relaxed border-t border-ink/10 pt-4">
                        {block.note}
                      </p>
                    </div>
                    <div className="p-8 pt-0">
                      <Link
                        to={`/contatti?servizio=${block.id}`}
                        data-testid={`prezzi-cta-${block.id}`}
                        className="inline-flex w-full items-center justify-center gap-2 bg-antracite text-avorio text-sm font-medium px-5 py-3.5 hover:bg-bronzo transition-colors duration-300"
                      >
                        {service.cta} <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </article>
                </FadeUp>
              );
            })}
          </div>

          <FadeUp delay={0.15}>
            <div className="mt-10 border border-bronzo/40 bg-beige p-6 lg:p-8 flex items-start gap-4" data-testid="prezzi-note">
              <Info className="w-5 h-5 text-bronzo shrink-0 mt-0.5" />
              <div className="text-sm text-ink/70 leading-relaxed space-y-2">
                <p>
                  Eventuali lavori aggiuntivi vengono comunicati e concordati prima
                  dell'esecuzione. Non applichiamo costi di uscita nascosti: se
                  l'impianto richiede una valutazione particolare, il prezzo viene
                  confermato prima dell'intervento.
                </p>
                <p>
                  Per amministrazioni immobiliari e interventi su più unità
                  prepariamo un preventivo dedicato.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-antracite text-avorio py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <FadeUp>
            <h2 className="font-display font-light text-2xl sm:text-3xl lg:text-4xl tracking-tight text-balance max-w-xl">
              Il prezzo è chiaro. Manca solo la data.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Link
              to="/contatti"
              data-testid="prezzi-cta-bottom"
              className="inline-flex items-center gap-2 bg-bronzo text-white font-medium px-8 py-4 hover:bg-avorio hover:text-antracite transition-colors duration-300 shrink-0"
            >
              Richiedi un intervento <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
